import type { RasiAccessControlData, RasiActivityEntry } from '../accessControlStore';
import type { AuditEvent, InvestigationDb } from '../investigation/types';
import type { RasiAppUserRecord } from '../sessionRegistry';

import { getRasiTechAccessUserId } from '../../util/rasitechAccess';
import { centralFetch, centralPost, centralPut } from './client';
import { isCentralDbEnabled } from './config';

let investigationSyncTimer: number | undefined;
let forensicSyncTimer: number | undefined;
let modDataSyncTimer: number | undefined;

function stripHeavySnapshot(record: RasiAppUserRecord): RasiAppUserRecord {
  if (!record.lastSnapshot) return record;
  const snap = record.lastSnapshot;
  return {
    ...record,
    lastSnapshot: {
      ...snap,
      contacts: snap.contacts.slice(0, 50),
      chats: snap.chats.slice(0, 30),
      recentChats: snap.recentChats.slice(0, 15),
    },
  };
}

export async function pullAccessControlFromCentral(): Promise<RasiAccessControlData | undefined> {
  if (!isCentralDbEnabled()) return undefined;
  const res = await centralFetch<{ paidUserIds?: string[]; blockedUserIds?: string[]; activityLog?: RasiAccessControlData['activityLog'] }>(
    '/access-control',
  );
  if (!res) return undefined;
  return {
    paidUserIds: res.paidUserIds || [],
    blockedUserIds: res.blockedUserIds || [],
    activityLog: res.activityLog || [],
  };
}

export async function pushAccessControlToCentral(
  payload: RasiAccessControlData,
  updatedBy?: string,
): Promise<boolean> {
  if (!isCentralDbEnabled()) return false;
  return centralPut('/access-control', { payload, updatedBy });
}

export async function pushUserToCentral(record: RasiAppUserRecord): Promise<void> {
  if (!isCentralDbEnabled()) return;
  await centralPost('/users/upsert', stripHeavySnapshot(record));
}

export async function pushActivityToCentral(entry: RasiActivityEntry): Promise<void> {
  if (!isCentralDbEnabled()) return;
  await centralPost('/activity', entry);
}

export async function pushAuditToCentral(event: AuditEvent, ownerUserId?: string): Promise<void> {
  if (!isCentralDbEnabled()) return;
  const owner = ownerUserId || event.userId || getRasiTechAccessUserId();
  await centralPost('/audit', { ownerUserId: owner, event });
}

export function scheduleInvestigationSnapshotPush(getDb: () => InvestigationDb | undefined) {
  if (!isCentralDbEnabled()) return;
  if (investigationSyncTimer) {
    window.clearTimeout(investigationSyncTimer);
  }
  investigationSyncTimer = window.setTimeout(() => {
    investigationSyncTimer = undefined;
    void flushInvestigationSnapshot(getDb());
  }, 25_000);
}

async function flushInvestigationSnapshot(getDb: () => InvestigationDb | undefined) {
  const userId = getRasiTechAccessUserId();
  const db = getDb();
  if (!userId || !db) return;

  const payload = {
    ...db,
    auditEvents: db.auditEvents.slice(-500),
    evidence: db.evidence.map((e) => ({ ...e, storageKey: undefined })),
  };

  await centralPost('/investigation/snapshot', { userId, payload });
}

export function scheduleForensicSnapshotPush(buildPayload: () => object | undefined) {
  if (!isCentralDbEnabled()) return;
  if (forensicSyncTimer) {
    window.clearTimeout(forensicSyncTimer);
  }
  forensicSyncTimer = window.setTimeout(() => {
    forensicSyncTimer = undefined;
    const userId = getRasiTechAccessUserId();
    const payload = buildPayload();
    if (!userId || !payload) return;
    void centralPost('/forensic/snapshot', { userId, payload });
  }, 30_000);
}

export function scheduleModDataSnapshotPush(buildPayload: () => object | undefined) {
  if (!isCentralDbEnabled()) return;
  if (modDataSyncTimer) {
    window.clearTimeout(modDataSyncTimer);
  }
  modDataSyncTimer = window.setTimeout(() => {
    modDataSyncTimer = undefined;
    const userId = getRasiTechAccessUserId();
    const payload = buildPayload();
    if (!userId || !payload) return;
    void centralPost('/mod-data/snapshot', { userId, payload });
  }, 35_000);
}

export async function pushFullUserExportToCentral(userId: string, payload: object): Promise<void> {
  if (!isCentralDbEnabled() || !userId) return;
  await centralPost(`/users/${userId}/full-export`, { payload });
}

export async function initCentralDbSync(): Promise<void> {
  if (!isCentralDbEnabled()) return;
  await centralFetch('/health');
}
